import {LogiCtrlRule, LogiCtrlRuleAction, LogiCtrlStatus} from "../../../../../api/LogiCtrl";
import {InventoryKind} from "../../../../../api/Logistics";
import {Typography} from "@mui/material";
import {green, grey, red} from "@mui/material/colors";
import {LogiCtrlButtons} from "./Buttons";
import {LogiCtrlError} from "./Error";

const defaultRule: LogiCtrlRule = {
    id: -1,
    project: -1,
    kind: null,
    opening: null,
    subtype: null,
    hardwareGroupItem: null,
    div10Item: null,
    glazingItem: null,
    miscProduct: null,
    inventoryRequest: null,
    action: null,
}

type SubItem = {
    label: string;
    ruleFilter: Partial<LogiCtrlRule>;
}

export function filterStatuses(statuses: LogiCtrlStatus[], filter: Partial<LogiCtrlRule>): LogiCtrlStatus[] {
    return statuses.filter(s => {

        if(filter.kind) {
            if(filter.kind !== s.kind) return false;
        }

        if(filter.opening) {
            if(filter.opening !== s.opening) return false;
        }

        if(filter.subtype) {
            if(filter.subtype !== s.subtype) return false;
        }

        if(filter.hardwareGroupItem) {
            if(filter.hardwareGroupItem !== s.hardwareGroupItem) return false;
        }

        if(filter.div10Item) {
            if(filter.div10Item !== s.div10) return false;
        }

        if(filter.glazingItem) {
            if(filter.glazingItem !== s.glazing) return false;
        }

        return true;
    })
}

export function getSubItems(kind: InventoryKind, statuses: LogiCtrlStatus[], filter: Partial<LogiCtrlRule>): SubItem[] | null {
    if (kind === "door") {
        const hasActive = filterStatuses(statuses, filter).some(s => s.subtype === "active");
        const hasInactive = filterStatuses(statuses, filter).some(s => {
            if(s.subtype !== "inactive") return false
            if(!!s.detailJson.items) return true; // include adding items
            if(!!s.detailJson.remove && !!s.error) return true; // include errored removals
            return false
        });

        const list: SubItem[] = [];

        if(hasActive) {
            list.push({label: "Active", ruleFilter: {subtype: "active"}})
        }

        if(hasInactive) {
            list.push({label: "Inactive", ruleFilter: {subtype: "inactive"}});
        }

        if(list.length === 0) return null;

        return list;
    }

    if (kind === "hardware") {
        const hwStatuses = statuses.filter(s => s.kind === "hardware" && s.displayName);
        const seen = new Set<string>();
        const items: SubItem[] = [];

        for (const s of hwStatuses) {
            const key = s.displayName!;

            if (seen.has(key)) continue;
            seen.add(key);

            if(!s.hardwareGroupItem) throw new Error("missing hardware group item for item");

            items.push({
                label: key,
                ruleFilter: {
                    ...filter,
                    hardwareGroupItem: s.hardwareGroupItem,
                },
            });
        }

        return items.length > 0 ? items : null;
    }

    return null;
}

export function hasSubItems(kind: InventoryKind): boolean {
    return kind === "door" || kind === "hardware";
}

export function LogiCtrlSubItems(props: {
    kind: InventoryKind;
    filter: Partial<LogiCtrlRule>;
    inherited: LogiCtrlRuleAction | null;
    rules: LogiCtrlRule[];
    statuses: LogiCtrlStatus[];
    onRuleChange: (old: LogiCtrlRule | null, rule: LogiCtrlRule) => void;
}) {
    const items = getSubItems(props.kind, props.statuses, props.filter);
    if (!items || items.length === 0) return null;

    return (
        <div style={{
            paddingLeft: 12,
            borderLeft: `2px solid ${grey["300"]}`,
            marginLeft: 4,
            marginTop: 4,
            display: "flex",
            flexDirection: "column",
            gap: 4,
        }}>
            {items.map((item, i) => (
                <SubItemRow
                    key={i}
                    label={item.label}
                    filter={{...props.filter, ...item.ruleFilter}}
                    inherited={props.inherited}
                    rules={props.rules}
                    statuses={props.statuses}
                    onRuleChange={props.onRuleChange}
                />
            ))}
        </div>
    );
}

export function SubItemRow(props: {
    label: string;
    filter: Partial<LogiCtrlRule>;
    rules: LogiCtrlRule[];
    inherited: LogiCtrlRuleAction | null;
    onRuleChange: (old: LogiCtrlRule | null, rule: LogiCtrlRule) => void;
    statuses: LogiCtrlStatus[];
}) {
    const existing = props.rules.find(r => {
        for (const k in props.filter) {
            if (r[k] !== props.filter[k]) return false;
        }

        return true;
    });

    const action = existing?.action || null;

    const setAction = (value: LogiCtrlRuleAction | null) => {
        if (existing) {
            props.onRuleChange(existing, {...existing, action: value});
            return;
        }

        props.onRuleChange(null, {
            ...defaultRule,
            ...props.filter,

            id: -1,
            action: value,
        });
    };

    const status = filterStatuses(props.statuses, props.filter);
    const error = status.find(s => !!s.error)?.error;

    return (
        <>
            <div style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 8,
            }}>
                <Typography variant="body2" sx={{fontSize: 12, color: "text.secondary"}}>
                    {props.label}
                </Typography>
                <div style={{display: "flex", alignItems: "center", gap: 4}}>
                    <div style={{width: 8, height: 8, borderRadius: "50%", backgroundColor: !!error ? red["600"] : green["600"], marginRight: 8}} />
                    <LogiCtrlButtons value={action} onChange={setAction} inherited={props.inherited} />
                </div>
            </div>
            {!!error && <LogiCtrlError error={error} />}
        </>
    );
}
